/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Map, List } from 'immutable';
import styles from './styles.scss';
// eslint-disable-next-line import/extensions
import { Swiper, SwiperSlide } from 'swiper/react';
// eslint-disable-next-line import/extensions
import 'swiper/css';
import SliderArrowButton from './components/arrow';
import { openModal } from '../../../../actions/component_actions';

const propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  isModal: PropTypes.bool,
};

function Gallery({ event, isModal }) {
  const logo = event.getIn(['logo', 'url']);
  const secondaryImages = event.getIn(['logo', 'secondaryImages']) || List();
  const title = event.get('title');
  const urls = [logo].concat(secondaryImages.toJS());
  const swiperRef = useRef();
  const [clickedSlide, setClickedSlide] = useState(0);
  const [isLeftButtonDisabled, setIsLeftButtonDisabled] = useState(false);
  const [isRightButtonDisabled, setIsRightButtonDisabled] = useState(false);
  const [currentImageSrc, setCurrentImageSrc] = useState(logo);

  const checkSwiperState = (swiper) => {
    if (swiper.isBeginning) {
      setIsLeftButtonDisabled(true);
      setIsRightButtonDisabled(false);
    }
    if (swiper.isEnd) {
      setIsLeftButtonDisabled(false);
      setIsRightButtonDisabled(true);
    }
    if (!swiper.isEnd && !swiper.isBeginning) {
      setIsLeftButtonDisabled(false);
      setIsRightButtonDisabled(false);
    }
  };

  const onBeforeInit = (swiper) => {
    swiperRef.current = swiper;
    checkSwiperState(swiper);
  };

  const handleOnTransitionSlide = useCallback((swiper) => {
    checkSwiperState(swiper);
  }, []);

  const preloadImages = useMemo(() => urls.map((url) => <img key={`${Math.round(Math.random() * 1000)}`} loading="eager" src={url} alt="x" />), []);

  const preLoadSlides = useMemo(
    () =>
      preloadImages.map((image, index) => (
        <SwiperSlide
          className={cn(styles.swiperSlide, { [styles.activeSlide]: clickedSlide === index })}
          onClick={() => {
            setCurrentImageSrc(image.props.src);
            setClickedSlide(swiperRef.current?.clickedIndex || 0);
          }}
          key={`${Math.round(Math.random() * 1000)}`}
        >
          <>{image}</>
        </SwiperSlide>
      )),
    [clickedSlide]
  );

  if (!logo) return <span />;

  if (secondaryImages && secondaryImages.size > 0) {
    return (
      <div className={cn(styles.gallery, { [styles.modal]: isModal })}>
        <div className={cn(styles.previewContainer, { [styles.modalPreview]: isModal })}>
          <img
            className={styles.preview}
            src={currentImageSrc}
            alt=""
            onClick={() => {
              window.store.dispatch(openModal({ modalName: 'galleryModal', data: { event, isModal: true } }));
            }}
          />
          <img className={styles.blurBackground} src={currentImageSrc} alt="x" />
        </div>
        <div style={{ maxWidth: '100%' }} className={cn(styles.carouselContainer, { [styles.modal]: isModal })}>
          <Swiper
            className={styles.carousel}
            autoplay
            onTransitionStart={handleOnTransitionSlide}
            onBeforeInit={onBeforeInit}
            slidesPerView={2}
            spaceBetween={6}
          >
            <SliderArrowButton key="left" disabled={isLeftButtonDisabled} direction="left" isModal={isModal} />
            {preLoadSlides}
            <SliderArrowButton key="right" disabled={isRightButtonDisabled} direction="right" isModal={isModal} />
          </Swiper>
        </div>
      </div>
    );
  }

  return (
    <ZoomWrapper scale={2.0}>
      <img src={logo} alt={title} className={styles.singlePreview} />
    </ZoomWrapper>
  );
}

Gallery.propTypes = propTypes;

export default Gallery;

const ZoomWrapper = ({ children, scale }) => {
  const [isZoomed, setIsZoomed] = useState(false);

  const handleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  const zoomStyle = {
    transform: isZoomed ? `scale(${scale})` : 'scale(1)',
    transition: 'transform 0.3s ease',
    zIndex: isZoomed ? 10000 : 'auto',
    position: 'relative',
    cursor: 'pointer',
  };

  return (
    <div style={{ cursor: 'pointer' }} onClick={handleZoom}>
      <div style={zoomStyle}>{children}</div>
    </div>
  );
};
